import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Heart, Feather, IndianRupee, Sparkles, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { openRazorpay } from "@/lib/razorpay";

export default function SupportPage() {
  const [config, setConfig] = useState(null);
  const [donors, setDonors] = useState([]);
  const [amount, setAmount] = useState(500);
  const [customMode, setCustomMode] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [processing, setProcessing] = useState(false);

  const loadDonors = () => api.get("/support/donors").then((r) => setDonors(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/payments/config").then((r) => setConfig(r.data));
    loadDonors();
  }, []);

  const donate = async () => {
    if (!form.name.trim()) { toast.error("नाम आवश्यक है"); return; }
    if (!amount || amount < 10) { toast.error("न्यूनतम राशि ₹10"); return; }
    setProcessing(true);
    try {
      const { data } = await api.post("/payments/donate", { ...form, amount: Number(amount) });
      const res = await openRazorpay({
        order_id: data.order_id, amount: data.amount, currency: data.currency, key_id: data.key_id,
        description: `Support Journalism — ₹${amount}`,
        prefill: { name: form.name, email: form.email, contact: form.phone },
      });
      if (res.ok) {
        toast.success("धन्यवाद! आपका सहयोग प्राप्त हुआ 🙏");
        setForm({ name: "", email: "", phone: "", message: "" });
        setAmount(500);
        loadDonors();
      }
    } catch (e) { toast.error(formatError(e)); }
    finally { setProcessing(false); }
  };

  const presets = config?.donation_presets || [100, 500, 1000, 2100, 5100];

  return (
    <>
      <PublicHeader />
      <section className="relative bg-gradient-to-br from-[#0F172A] via-[#111827] to-[#1E3A8A] text-white overflow-hidden" data-testid="support-hero">
        <div className="absolute -top-16 -right-10 opacity-[0.05] pointer-events-none text-[360px] font-devnagri-heading font-black leading-none select-none">♥</div>
        <div className="max-w-5xl mx-auto px-4 py-16 md:py-20 relative">
          <div className="flex items-center gap-3 mb-4">
            <Heart className="w-5 h-5 text-red-400"/>
            <span className="uppercase tracking-[0.3em] text-[11px] text-amber-300 font-black">Support Journalism · पत्रकारिता का साथ दें</span>
          </div>
          <h1 className="font-devnagri-heading text-4xl sm:text-5xl md:text-6xl font-black leading-tight max-w-3xl">
            निर्भीक पत्रकारिता का <span className="text-amber-300">सहयोग करें</span>
          </h1>
          <p className="text-slate-300 mt-5 max-w-2xl leading-relaxed">
            आपका छोटा-सा सहयोग INDIAN-NEWS-21 को स्वतंत्र, ज़मीनी और निष्पक्ष रखता है — जहाँ ख़बर के पीछे कोई विज्ञापनदाता नहीं, बस <b className="text-amber-300">JAKIR MOMIN</b> की क़लम और पाठकों का भरोसा है।
          </p>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 py-12 grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="support-page">
        <div className="lg:col-span-3 bg-white rounded-2xl shadow-lg border border-slate-200 p-6 md:p-8">
          <div className="flex items-center gap-2 mb-4">
            <IndianRupee className="w-5 h-5 text-emerald-600"/>
            <h2 className="font-bold text-xl text-[#0F172A]">राशि चुनें</h2>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
            {presets.map((a) => (
              <button key={a} onClick={() => { setAmount(a); setCustomMode(false); }}
                className={`py-3 rounded-lg border-2 font-bold text-lg transition ${
                  amount === a && !customMode ? "border-[#EA580C] bg-orange-50 text-[#EA580C]" : "border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`preset-${a}`}>
                ₹{a}
              </button>
            ))}
          </div>
          <div className="mb-6">
            <button onClick={() => setCustomMode(!customMode)} className="text-sm text-[#1E3A8A] hover:underline" data-testid="custom-amount-toggle">
              {customMode ? "← पूर्व-निर्धारित राशि" : "कस्टम राशि दें →"}
            </button>
            {customMode && (
              <div className="mt-2 relative">
                <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <Input type="number" min="10" max="100000" value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-9 text-lg font-bold" placeholder="अपनी इच्छित राशि" data-testid="custom-amount"/>
              </div>
            )}
          </div>

          <h3 className="font-bold text-[#0F172A] mb-3">आपकी जानकारी</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <Input placeholder="आपका नाम *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="donor-name"/>
            <Input type="email" placeholder="ईमेल (रसीद के लिए)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="donor-email"/>
          </div>
          <Input placeholder="फ़ोन (वैकल्पिक)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mb-3" data-testid="donor-phone"/>
          <Textarea rows={3} placeholder="कोई संदेश (वैकल्पिक) — प्रधान संपादक तक पहुँचेगा"
            value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} maxLength={500} data-testid="donor-message"/>

          <Button onClick={donate} disabled={processing || !config?.enabled}
            className="w-full mt-6 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold py-6 text-lg shadow-lg"
            data-testid="donate-btn">
            <Heart className="w-5 h-5 mr-2"/>
            {processing ? "प्रक्रिया हो रही…" : `₹${amount || 0} दान करें`}
          </Button>
          <div className="flex items-center justify-center gap-2 text-xs text-slate-500 mt-3">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600"/>
            सुरक्षित भुगतान · Razorpay · UPI/Cards/NetBanking
          </div>
        </div>

        <aside className="lg:col-span-2 space-y-6">
          <div className="bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white rounded-2xl p-6 shadow-lg">
            <Feather className="w-6 h-6 text-amber-400 mb-2"/>
            <blockquote className="italic text-sm leading-relaxed">
              &ldquo;हम विज्ञापन नहीं, विश्वास बेचते हैं। आप जैसे पाठकों का सहयोग ही हमारी असली पूँजी है।&rdquo;
            </blockquote>
            <div className="mt-4">
              <div className="text-amber-300 font-black text-lg italic tracking-wider" style={{ fontFamily: "'Times New Roman', serif" }}>JAKIR MOMIN</div>
              <div className="text-slate-300 text-xs">Chief Editor · INDIAN-NEWS-21</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="font-bold text-[#0F172A] mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500"/>हाल के सहयोगकर्ता</h3>
            {donors.length === 0 ? (
              <div className="text-xs text-slate-400 italic">सबसे पहले सहयोगकर्ता बनें!</div>
            ) : (
              <ul className="space-y-2.5">
                {donors.slice(0, 8).map((d, i) => (
                  <li key={`${d.name}-${d.at || i}`} className="flex items-center justify-between border-b border-slate-100 pb-2" data-testid={`donor-${i}`}>
                    <div className="min-w-0">
                      <div className="font-bold text-sm text-[#0F172A] truncate">{d.name}</div>
                      {d.message && <div className="text-[10px] text-slate-500 italic truncate">"{d.message}"</div>}
                    </div>
                    <div className="text-emerald-700 font-bold text-sm shrink-0 ml-2">₹{d.amount}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </main>
      <PublicFooter />
    </>
  );
}
